import { useEffect, useState } from 'react';
import { abfrageHalten } from '../adresse';
import { Icon } from './Icon';
import { Kommentare } from './Kommentare';
import { toast } from './toast';
import { Field, listParse } from './fields';
import { mdHtml } from '../logik/md.js';
import { byId, processPath } from '../logik/model.js';
import { useVerantwortlichVorschlaege } from './verantwortlich';
import { fmtDate } from '../logik/export/report.js';
import * as A from '../logik/actions.js';
import { aufServer, sync } from '../sync';
import { menueOeffnen, adresse, kopieren } from './Kontextmenue';
import { bereinigen } from '../kontextmenue';
import { frage } from './frage';
import { ExportKnopf } from './ListenExport';

const Md = ({ text }: { text: string }) => <div className="md" dangerouslySetInnerHTML={{ __html: mdHtml(text) }} />;

function Variant({ v, chosen }: { v: any; chosen: boolean }) {
  return (
    <div className={'variant' + (chosen ? ' chosen' : '')}>
      <h4>{v.key}: {v.titel}{chosen ? ' ✓' : ''}</h4>
      {v.beschreibung && <Md text={v.beschreibung} />}
      {v.pro?.length > 0 && <ul className="pro">{v.pro.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>}
      {v.contra?.length > 0 && <ul className="con">{v.contra.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>}
    </div>
  );
}

const stamp = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });

/** Anzeige bzw. Bearbeitung einer Entscheidung (im Panel und in der Übersicht). */
export function DecisionBlock({ data, d, edit, compact }: { data: any; d: any; edit: boolean; compact?: boolean }) {
  const done = d.status === 'entschieden';
  const [why, setWhy] = useState('');
  const set = (f: string) => (v: any) => A.setDecisionField(d.id, f, v);
  const vorschlaege = useVerantwortlichVorschlaege(data);
  if (!edit) {
    return (
      <div>
        <div className="top" style={{ fontSize: '12px', color: 'var(--pa-text-muted)', marginBottom: '4px' }}>
          <strong style={{ color: done ? 'var(--pa-ok)' : 'var(--pa-decision)' }}>{done ? (d.gewaehlt ? 'Entschieden: Variante ' + d.gewaehlt : 'Entschieden') : 'Offen'}</strong>
          {d.termin && <> · Termin {fmtDate(d.termin)}</>}{d.verantwortlich && <> · {d.verantwortlich}</>}
          {d.geaendertVon && <> · geändert von {d.geaendertVon}{d.geaendertAm ? ', ' + stamp(d.geaendertAm) : ''}</>}
        </div>
        {compact && <div style={{ fontWeight: 600, marginBottom: '6px' }}>{d.frage}</div>}
        {d.hintergrund && <Md text={d.hintergrund} />}
        <div className="variants" style={compact ? { gridTemplateColumns: '1fr' } : undefined}>{d.varianten.map((v: any) => <Variant key={v.key} v={v} chosen={d.gewaehlt === v.key} />)}</div>
        {d.empfehlung && <div className="empf"><strong>Empfehlung:</strong><Md text={d.empfehlung} /></div>}
        {d.begruendung && <div className="empf"><strong>Begründung der Entscheidung:</strong><Md text={d.begruendung} /></div>}
      </div>
    );
  }
  return (
    <div>
      <Field label="Fragestellung" value={d.frage} onCommit={set('frage')} multiline />
      <Field label="Hintergrund (Markdown)" value={d.hintergrund} onCommit={set('hintergrund')} multiline />
      {d.varianten.map((v: any, i: number) => (
        <div className="variant" style={{ marginBottom: '8px' }} key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><strong>Variante {v.key}</strong>
            <button className="btn icon small" title="Variante entfernen" onClick={() => A.removeVariant(d.id, i)}><Icon name="x" /></button></div>
          <Field label="Titel" value={v.titel} onCommit={(x) => A.setVariantField(d.id, i, 'titel', x)} />
          <Field label="Beschreibung (Markdown)" value={v.beschreibung} onCommit={(x) => A.setVariantField(d.id, i, 'beschreibung', x)} multiline />
          <div className="grid2">
            <Field label="Vorteile (je Zeile)" value={v.pro} format={(a) => (a || []).join('\n')} parse={listParse('\n')} onCommit={(x) => A.setVariantField(d.id, i, 'pro', x)} multiline />
            <Field label="Nachteile (je Zeile)" value={v.contra} format={(a) => (a || []).join('\n')} parse={listParse('\n')} onCommit={(x) => A.setVariantField(d.id, i, 'contra', x)} multiline />
          </div>
          {!done && <button className="btn small primary" onClick={async () => { if (await frage(`Variante ${v.key} „${v.titel}“ übernehmen? Der Schritt im Prozess wird entsprechend umgestellt.`, { ja: 'Übernehmen' })) { A.decide(d.id, i, why); toast('Entscheidung übernommen.'); } }}>Variante {v.key} übernehmen</button>}
        </div>
      ))}
      <button className="btn small" onClick={() => A.addVariant(d.id)}><Icon name="plus" /> Variante</button>
      <div style={{ marginTop: '10px' }}>
        <Field label="Empfehlung (Markdown)" value={d.empfehlung} onCommit={set('empfehlung')} multiline />
        <div className="grid2">
          <Field label="Verantwortlich" value={d.verantwortlich} onCommit={set('verantwortlich')} suggest={vorschlaege} />
          <Field label="Termin" type="date" value={d.termin} onCommit={set('termin')} />
        </div>
        {!done && <Field label="Begründung (wird bei Übernahme gespeichert)" value={why} onCommit={setWhy} multiline md />}
        {done && <button className="btn small" onClick={() => A.reopenDecision(d.id)}>Entscheidung wieder öffnen</button>}
      </div>
    </div>
  );
}

interface DecisionsPageProps {
  data: any;
  onNavigate: (pid: string, nodeId?: string) => void;
  /** Ohne Bearbeitungsrecht nur Anzeige. */
  darfBearbeiten?: boolean;
}

export function DecisionsPage({ data, onNavigate, darfBearbeiten = true }: DecisionsPageProps) {
  // Filter aus der Adresse (Sprung aus dem Cockpit, Lesezeichen): ?status=offen|entschieden|alle – und bei Änderung dorthin zurück
  const [filter, setFilter] = useState(() => { const s = new URLSearchParams(location.search).get('status'); return s === 'entschieden' || s === 'alle' ? s : 'offen'; });
  useEffect(() => { abfrageHalten({ status: [filter, 'offen'] }); });
  const [editId, setEditId] = useState<string | null>(null);
  const passt = (f: string, d: any) => f === 'alle' || (f === 'offen' ? d.status !== 'entschieden' : d.status === 'entschieden');
  const list = data.decisions.filter((d: any) => passt(filter, d));
  const sorted = [...list].sort((a: any, b: any) => (a.termin || '9999').localeCompare(b.termin || '9999'));
  return (
    <div className="decisions" data-testid="decisions">
      <div className="inner">
        <h1>Entscheidungen</h1>
        {darfBearbeiten && (
          <div className="hint">Offene Punkte, die den Prozess beeinflussen. Mit „Bearbeiten“ lassen sich Frage, Varianten, Empfehlung und Termin pflegen; „Variante übernehmen“ macht die Entscheidung zum Teil des Prozesses.<br />
            Neue Entscheidung: im Prozess den Schritt anklicken, nach dem entschieden werden muss, und „Offene Entscheidung danach einfügen“ wählen.</div>
        )}
        {!darfBearbeiten && <div className="hint">Offene Punkte, die den Prozess beeinflussen.</div>}
        <div className="filterbar">
          {['offen', 'entschieden', 'alle'].map((f) => <button key={f} className={'btn small' + (filter === f ? ' on' : '')} onClick={() => setFilter(f)}>{f[0].toUpperCase() + f.slice(1)} ({data.decisions.filter((d: any) => passt(f, d)).length})</button>)}
          <span style={{ flex: 1 }} />
          <ExportKnopf projektId={sync.projektId} bereich="entscheidungen" />
        </div>
        {sorted.length === 0 && <div className="empty-state">Keine Einträge.</div>}
        {sorted.map((d: any) => {
          const path = processPath(data, d.processId).map((p: any) => p.titel).join(' › ');
          const bearbeiten = darfBearbeiten && editId === d.id;
          return (
            <div key={d.id} className={'dcard' + (d.status === 'entschieden' ? ' done' : '')} data-testid={'dcard-' + d.id}
              onContextMenu={(e) => menueOeffnen(e, bereinigen([
                { text: editId === d.id ? 'Bearbeiten beenden' : 'Bearbeiten', icon: editId === d.id ? 'eye' : 'edit', aktion: () => setEditId(editId === d.id ? null : d.id), wenn: darfBearbeiten, testid: 'km-bearbeiten' },
                { text: 'Im Prozess zeigen', icon: 'target', aktion: () => onNavigate(d.processId, d.nodeId), wenn: !!(d.nodeId && byId(data.nodes, d.nodeId)) },
                { text: 'Link kopieren', icon: 'link', aktion: () => kopieren(adresse(`/p/${encodeURIComponent(sync.projektId)}?zu=entscheidung&id=${encodeURIComponent(d.id)}`)) },
              ]))}>
              <div className="top"><strong>{d.id}</strong><span>{path}</span>
                <span style={{ flex: 1 }}></span>
                {darfBearbeiten && <button className={'btn small' + (editId === d.id ? ' on' : '')} onClick={() => setEditId(editId === d.id ? null : d.id)}><Icon name={editId === d.id ? 'eye' : 'edit'} /> {editId === d.id ? 'Fertig' : 'Bearbeiten'}</button>}
                {d.nodeId && byId(data.nodes, d.nodeId) && <button className="btn small" onClick={() => onNavigate(d.processId, d.nodeId)}><Icon name="target" /> Im Prozess zeigen</button>}
              </div>
              {!bearbeiten && <h3>{d.frage}</h3>}
              <DecisionBlock data={data} d={d} edit={bearbeiten} />
              {d.quellen?.length > 0 && <div className="hint" style={{ marginTop: '8px' }}>Quellen: {d.quellen.map((q: any) => q.dokument + (q.datum ? ' (' + fmtDate(q.datum) + ')' : '')).join('; ')}</div>}
              {!bearbeiten && aufServer('decisions', d.id) && <Kommentare bezugTyp="entscheidung" bezugId={d.id} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
